import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import apiService, {
  type ChatUsageConversationDetail,
  type ChatUsageFilters,
  type ChatUsageSummary,
  type ChatUsageTurnDetail,
} from "../services/api";

const DEFAULT_LOOKBACK_DAYS = 30;
const CONVERSATIONS_PAGE_SIZE = 25;

const INTENT_LABELS: Record<string, string> = {
  own_metrics: "Own metrics",
  own_programs: "Own programs",
  own_brands: "Brands in own content",
  create_collection: "Create collection",
  media_kit: "Media kit link",
  program_ranking: "Program ranking",
  program_views: "Program / channel views",
  brand_research: "Brand research",
  sponsor_market: "Sponsor market",
  livestreams: "Livestreams",
  verify_export: "Verify / export",
  entity_lookup: "Entity lookup",
  how_to_use: "How to use",
  other: "Other",
  no_tools: "No tools",
  unclassified: "Not classified yet",
  unknown: "Unknown",
};

const ACTION_LABELS: Record<string, string> = {
  conversation_created: "Conversations started",
  conversation_deleted: "Conversations deleted by user",
  entity_link_click: "Entity link clicks",
  link_click: "Other link clicks",
  collection_form_shown: "Collection forms shown",
  collection_link_click: "Collection card clicks",
  collection_saved: "Collections saved from chat",
  verify_export_shown: "Export forms shown",
  verify_export_click: "Export card clicks",
  stop_generation: "Stopped generation",
};

const intentLabel = (intent: string | null | undefined) =>
  intent ? INTENT_LABELS[intent] ?? intent : "—";

function ymdDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const pct = (v: number | null | undefined): string =>
  v == null || Number.isNaN(v) ? "—" : `${(v * 100).toFixed(1)}%`;

/** Chat questions cost fractions of a cent, so small amounts keep more decimals. */
function formatUsd(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  if (n >= 100) return `$${n.toFixed(0)}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n >= 0.01) return `$${n.toFixed(3)}`;
  if (n > 0) return `$${n.toFixed(4)}`;
  return "$0";
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(Math.round(n));
}

const formatMs = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`);

const formatDateTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" }) : "—";

function StatCard({
  value,
  label,
  tooltip,
  color,
}: {
  value: React.ReactNode;
  label: string;
  tooltip?: string;
  color?: string;
}) {
  const card = (
    <Paper variant="outlined" sx={{ p: 2, height: "100%", cursor: tooltip ? "help" : "default" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, color }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={tooltip ? { textDecoration: "underline dotted", textUnderlineOffset: 3 } : undefined}
      >
        {label}
      </Typography>
    </Paper>
  );
  return tooltip ? (
    <MuiTooltip title={tooltip} arrow placement="top">
      {card}
    </MuiTooltip>
  ) : (
    card
  );
}

function SectionTitle({ children, caption }: { children: React.ReactNode; caption?: string }) {
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {children}
      </Typography>
      {caption && (
        <Typography variant="body2" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Box>
  );
}

interface Column<T> {
  key: string;
  label: string;
  align?: "left" | "right";
  render: (row: T) => React.ReactNode;
  /** Present when the column is sortable. */
  sortValue?: (row: T) => number | string;
}

function SortableTable<T>({
  rows,
  columns,
  rowKey,
  defaultSort,
  onRowClick,
  maxRows,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  defaultSort: string;
  onRowClick?: (row: T) => void;
  maxRows?: number;
}) {
  const [sort, setSort] = useState<{ key: string; direction: "asc" | "desc" }>({
    key: defaultSort,
    direction: "desc",
  });
  const sorted = useMemo(() => {
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return rows;
    const mult = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = column.sortValue!(a);
      const bv = column.sortValue!(b);
      return typeof av === "string" || typeof bv === "string"
        ? mult * String(av).localeCompare(String(bv))
        : mult * (av - bv);
    });
  }, [rows, columns, sort]);
  const visible = maxRows ? sorted.slice(0, maxRows) : sorted;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.key} align={c.align ?? "left"}>
                {c.sortValue ? (
                  <TableSortLabel
                    active={sort.key === c.key}
                    direction={sort.key === c.key ? sort.direction : "desc"}
                    onClick={() =>
                      setSort((prev) => ({
                        key: c.key,
                        direction: prev.key === c.key && prev.direction === "desc" ? "asc" : "desc",
                      }))
                    }
                  >
                    {c.label}
                  </TableSortLabel>
                ) : (
                  c.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Typography variant="body2" color="text.secondary">
                  No data in this range.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {visible.map((row) => (
            <TableRow
              key={rowKey(row)}
              hover={Boolean(onRowClick)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={onRowClick ? { cursor: "pointer" } : undefined}
            >
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align ?? "left"}>
                  {c.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

type CompanyRow = ChatUsageSummary["companies"][number];
type UserRow = ChatUsageSummary["users"][number];
type CostTurnRow = ChatUsageSummary["topCostTurns"][number];

const groupColumns = <T extends CompanyRow | UserRow>(): Column<T>[] => [
  { key: "questions", label: "Questions", align: "right", render: (r) => r.questions, sortValue: (r) => r.questions },
  { key: "conversations", label: "Chats", align: "right", render: (r) => r.conversations, sortValue: (r) => r.conversations },
  { key: "costUsd", label: "Cost", align: "right", render: (r) => formatUsd(r.costUsd), sortValue: (r) => r.costUsd },
  {
    key: "costPerQuestion",
    label: "$/question",
    align: "right",
    render: (r) => formatUsd(r.questions ? r.costUsd / r.questions : 0),
    sortValue: (r) => (r.questions ? r.costUsd / r.questions : 0),
  },
  { key: "clicks", label: "Link clicks", align: "right", render: (r) => r.clicks, sortValue: (r) => r.clicks },
  {
    key: "collectionsSaved",
    label: "Collections",
    align: "right",
    render: (r) => r.collectionsSaved,
    sortValue: (r) => r.collectionsSaved,
  },
  { key: "errors", label: "Errors", align: "right", render: (r) => r.errors, sortValue: (r) => r.errors },
  { key: "topIntent", label: "Top intent", render: (r) => intentLabel(r.topIntent) },
  {
    key: "lastActiveAt",
    label: "Last active",
    render: (r) => formatDateTime(r.lastActiveAt),
    sortValue: (r) => r.lastActiveAt ?? "",
  },
];

function ToolCallList({ tools }: { tools: ChatUsageTurnDetail["tools"] }) {
  if (tools.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No tools called
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5}>
      {tools.map((tool, i) => (
        <MuiTooltip key={i} title={tool.args ?? ""} placement="left">
          <Typography variant="caption" sx={{ fontFamily: "monospace", color: tool.ok ? undefined : "error.main" }}>
            {tool.name} · {formatMs(tool.durationMs)}
            {tool.error ? ` · ${tool.error}` : ""}
          </Typography>
        </MuiTooltip>
      ))}
    </Stack>
  );
}

function TurnMeta({ turn }: { turn: ChatUsageTurnDetail }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: "action.hover" }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Chip size="small" label={intentLabel(turn.intent ?? "unclassified")} color="primary" variant="outlined" />
        {turn.source === "live" && turn.intent && turn.toolIntent !== turn.intent && turn.toolIntent !== "no_tools" && (
          <MuiTooltip title="What the model did, based on the tools it called">
            <Chip size="small" label={`Tools: ${intentLabel(turn.toolIntent)}`} variant="outlined" />
          </MuiTooltip>
        )}
        {turn.topic && <Chip size="small" label={turn.topic} />}
        {turn.status !== "ok" && <Chip size="small" color="warning" label={turn.status} />}
        {turn.source === "backfill" && <Chip size="small" label="backfill (no cost data)" />}
      </Stack>
      {turn.source === "live" && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
          {formatUsd(turn.modelCostUsd + turn.classifierCostUsd)} · {turn.modelCalls} model calls ·{" "}
          {formatTokens(turn.promptTokens)} in ({formatTokens(turn.cachedTokens)} cached) /{" "}
          {formatTokens(turn.completionTokens)} out · {formatMs(turn.latencyMs)} · {turn.model}
        </Typography>
      )}
      <ToolCallList tools={turn.tools} />
      {turn.events.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {turn.events.map((e, i) => (
            <MuiTooltip key={i} title={e.payload ? JSON.stringify(e.payload) : ""}>
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`${ACTION_LABELS[e.type] ?? e.type}${
                  typeof e.payload?.["label"] === "string" ? `: ${e.payload["label"]}` : ""
                }`}
              />
            </MuiTooltip>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

function ConversationDrawer({ conversationId, onClose }: { conversationId: string | null; onClose: () => void }) {
  const { data, isFetching, error } = useQuery<ChatUsageConversationDetail>({
    queryKey: ["chatUsageConversation", conversationId],
    queryFn: () => apiService.getChatUsageConversation(conversationId!),
    enabled: Boolean(conversationId),
  });

  return (
    <Drawer anchor="right" open={Boolean(conversationId)} onClose={onClose}>
      <Box sx={{ width: { xs: "100vw", md: 720 }, p: 3 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {data?.title ?? "Conversation"}
              {data?.deletedAt && (
                <MuiTooltip title={`Deleted ${formatDateTime(data.deletedAt)}`}>
                  <Chip size="small" color="default" label="Deleted by user" sx={{ ml: 1, verticalAlign: "middle" }} />
                </MuiTooltip>
              )}
            </Typography>
            {data && (
              <Typography variant="body2" color="text.secondary">
                {data.userName} ({data.userEmail}) · {data.companyName} · {formatUsd(data.totalCostUsd)} ·{" "}
                {formatDateTime(data.createdAt)}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
        {isFetching && !data && <CircularProgress size={24} />}
        {error && <Alert severity="error">Failed to load conversation.</Alert>}
        {data && (
          <Stack spacing={2} divider={<Divider flexItem />}>
            {data.messages.map((m, i) => (
              <Box key={i}>
                <Typography variant="overline" color="text.secondary">
                  {m.role === "user" ? "User" : "Assistant"} · {formatDateTime(m.createdAt)}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {m.text || (m.role === "assistant" ? "(no answer)" : "")}
                </Typography>
                {m.error && (
                  <Typography variant="body2" color="error">
                    {m.error}
                  </Typography>
                )}
                {m.collectionLinks ? (
                  <Chip size="small" sx={{ mt: 0.5 }} label={`${m.collectionLinks} collection card(s) shown`} />
                ) : null}
                {m.verifyExports ? (
                  <Chip size="small" sx={{ mt: 0.5, ml: 0.5 }} label={`${m.verifyExports} export card(s) shown`} />
                ) : null}
                {m.turns?.map((turn) => <TurnMeta key={turn.turnId} turn={turn} />)}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

function BreakdownTable({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ label: string; value: React.ReactNode; secondary?: React.ReactNode; onClick?: () => void }>;
}) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell colSpan={3}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography variant="body2" color="text.secondary">
                  No data.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((r) => (
            <TableRow
              key={r.label}
              hover={Boolean(r.onClick)}
              onClick={r.onClick}
              sx={r.onClick ? { cursor: "pointer" } : undefined}
            >
              <TableCell>{r.label}</TableCell>
              <TableCell align="right">{r.value}</TableCell>
              <TableCell align="right" sx={{ color: "text.secondary" }}>
                {r.secondary ?? ""}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ChatUsageAdmin() {
  const theme = useTheme();
  const [dateFrom, setDateFrom] = useState(ymdDaysAgo(DEFAULT_LOOKBACK_DAYS));
  const [dateTo, setDateTo] = useState(ymdDaysAgo(0));
  const [applied, setApplied] = useState<ChatUsageFilters>({
    dateFrom: ymdDaysAgo(DEFAULT_LOOKBACK_DAYS),
    dateTo: ymdDaysAgo(0),
  });
  const [labels, setLabels] = useState<{ company?: string; user?: string }>({});
  const [intent, setIntent] = useState("");
  const [page, setPage] = useState(0);
  const [openConversation, setOpenConversation] = useState<string | null>(null);

  const { data, isFetching, error } = useQuery<ChatUsageSummary>({
    queryKey: ["chatUsageSummary", applied],
    queryFn: () => apiService.getChatUsageSummary(applied),
    staleTime: 60_000,
  });

  const conversationsQuery = useQuery({
    queryKey: ["chatUsageConversations", applied, intent, page],
    queryFn: () =>
      apiService.getChatUsageConversations({
        ...applied,
        ...(intent ? { intent } : {}),
        page,
        pageSize: CONVERSATIONS_PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const applyFilters = (patch: Partial<ChatUsageFilters>, nextLabels = labels) => {
    setApplied((prev) => {
      const next = { ...prev, ...patch };
      if (!next.companyId) delete next.companyId;
      if (!next.userId) delete next.userId;
      return next;
    });
    setLabels(nextLabels);
    setPage(0);
  };

  const chartData = useMemo(
    () =>
      (data?.daily ?? []).map((d) => ({
        date: d.date.slice(5),
        Questions: d.questions,
        "Link clicks": d.clicks,
        "Cost $": Number(d.costUsd.toFixed(4)),
      })),
    [data],
  );

  const companyColumns = useMemo<Column<CompanyRow>[]>(
    () => [
      { key: "name", label: "Company", render: (r) => r.name, sortValue: (r) => r.name },
      { key: "users", label: "Users", align: "right", render: (r) => r.users, sortValue: (r) => r.users },
      ...groupColumns<CompanyRow>(),
    ],
    [],
  );
  const userColumns = useMemo<Column<UserRow>[]>(
    () => [
      {
        key: "name",
        label: "User",
        render: (r) => (
          <Box>
            <Typography variant="body2">{r.name || r.email || r.userId}</Typography>
            {r.name && (
              <Typography variant="caption" color="text.secondary">
                {r.email}
              </Typography>
            )}
          </Box>
        ),
        sortValue: (r) => r.name || r.email,
      },
      { key: "companyName", label: "Company", render: (r) => r.companyName, sortValue: (r) => r.companyName },
      ...groupColumns<UserRow>(),
    ],
    [],
  );
  const costColumns = useMemo<Column<CostTurnRow>[]>(
    () => [
      {
        key: "question",
        label: "Question",
        render: (r) => (
          <Typography variant="body2" sx={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {r.question}
          </Typography>
        ),
      },
      { key: "user", label: "User", render: (r) => `${r.userName} · ${r.companyName}` },
      { key: "intent", label: "Intent", render: (r) => intentLabel(r.intent) },
      { key: "modelCalls", label: "Model calls", align: "right", render: (r) => r.modelCalls, sortValue: (r) => r.modelCalls },
      { key: "toolCalls", label: "Tools", align: "right", render: (r) => r.toolCalls, sortValue: (r) => r.toolCalls },
      {
        key: "tokens",
        label: "Tokens in/out",
        align: "right",
        render: (r) => `${formatTokens(r.promptTokens)} / ${formatTokens(r.completionTokens)}`,
        sortValue: (r) => r.promptTokens,
      },
      { key: "costUsd", label: "Cost", align: "right", render: (r) => formatUsd(r.costUsd), sortValue: (r) => r.costUsd },
      { key: "createdAt", label: "When", render: (r) => formatDateTime(r.createdAt), sortValue: (r) => r.createdAt },
    ],
    [],
  );

  const t = data?.totals;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Chat usage
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
        What users ask the assistant, what they do with the answers, and what it costs. Dates are Chilean time.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} flexWrap="wrap" useFlexGap>
          <TextField
            label="Date from"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Date to"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" onClick={() => applyFilters({ dateFrom, dateTo })} disabled={isFetching}>
            {isFetching ? "Loading…" : "Run"}
          </Button>
          {applied.companyId && (
            <Chip
              label={`Company: ${labels.company ?? applied.companyId}`}
              onDelete={() => applyFilters({ companyId: undefined }, { ...labels, company: undefined })}
            />
          )}
          {applied.userId && (
            <Chip
              label={`User: ${labels.user ?? applied.userId}`}
              onDelete={() => applyFilters({ userId: undefined }, { ...labels, user: undefined })}
            />
          )}
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load chat usage. {(error as Error).message}
        </Alert>
      )}

      {isFetching && !data && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {data && t && (
        <Stack spacing={3}>
          {data.truncated && (
            <Alert severity="warning">This range has more rows than the summary reads; narrow the dates.</Alert>
          )}
          {t.backfilledQuestions > 0 && (
            <Alert severity="info">
              {t.backfilledQuestions} questions were backfilled from before tracking started and have no cost, latency or
              tool data. Cost per question only uses tracked questions.
            </Alert>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 2 }}>
            <StatCard value={t.questions.toLocaleString()} label="Questions" />
            <StatCard value={t.conversations.toLocaleString()} label="Conversations" />
            <StatCard value={t.activeUsers} label="Active users" />
            <StatCard value={t.activeCompanies} label="Active companies" />
            <StatCard
              value={formatUsd(t.totalCostUsd)}
              label="Total cost"
              color={theme.palette.error.main}
              tooltip={`Answers ${formatUsd(t.modelCostUsd)} + question classifier ${formatUsd(t.classifierCostUsd)}. Tokens: ${formatTokens(t.promptTokens)} in (${formatTokens(t.cachedTokens)} cached), ${formatTokens(t.completionTokens)} out.`}
            />
            <StatCard
              value={formatUsd(t.costPerQuestion)}
              label="Cost per question"
              tooltip="Total cost divided by tracked (non-backfilled) questions."
            />
            <StatCard
              value={pct(t.clickThroughRate)}
              label="Link click-through"
              tooltip={`${t.answersClicked} of ${t.answersWithLinks} answers with links got at least one click. ${t.entityClicks} entity clicks, ${t.linkClicks} other link clicks.`}
            />
            <StatCard
              value={`${t.collectionsSaved} / ${t.collectionsShown}`}
              label="Collections saved / shown"
              tooltip={`The assistant showed ${t.collectionsShown} prefilled collection forms; users clicked ${t.collectionLinkClicks} and saved ${t.collectionsSaved}.`}
            />
            <StatCard
              value={`${t.verifyExportClicks} / ${t.verifyExportsShown}`}
              label="Export cards opened / shown"
              tooltip={`The assistant showed ${t.verifyExportsShown} prefilled PDF/Excel export forms (verify actions); users opened ${t.verifyExportClicks}.`}
            />
            <StatCard
              value={pct(t.errorRate)}
              label="Error rate"
              color={t.errorRate > 0.05 ? theme.palette.warning.dark : undefined}
              tooltip={`Aborted by user: ${pct(t.abortRate)}. Hit the model-turn limit: ${pct(t.maxTurnsRate)}. Rate limited: ${t.rateLimited}.`}
            />
            <StatCard value={`${formatMs(t.p50LatencyMs)} / ${formatMs(t.p95LatencyMs)}`} label="Latency p50 / p95" />
          </Box>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Daily questions and cost
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="count" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis
                    yAxisId="cost"
                    orientation="right"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v: number) => formatUsd(v)}
                  />
                  <Tooltip formatter={(value: any, name: any) => (name === "Cost $" ? formatUsd(Number(value)) : value)} />
                  <Legend />
                  <Bar yAxisId="count" dataKey="Questions" fill={theme.palette.primary.main} />
                  <Bar yAxisId="count" dataKey="Link clicks" fill={theme.palette.success.light} />
                  <Line yAxisId="cost" dataKey="Cost $" stroke={theme.palette.error.main} dot={false} strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <SectionTitle caption="Click a row to filter the whole page by that company.">By company</SectionTitle>
          <SortableTable
            rows={data.companies}
            columns={companyColumns}
            rowKey={(r) => r.companyId}
            defaultSort="questions"
            onRowClick={(r) =>
              applyFilters({ companyId: r.companyId }, { ...labels, company: r.name })
            }
          />

          <SectionTitle caption="Click a row to filter the whole page by that user.">By user</SectionTitle>
          <SortableTable
            rows={data.users}
            columns={userColumns}
            rowKey={(r) => r.userId}
            defaultSort="questions"
            onRowClick={(r) => applyFilters({ userId: r.userId }, { ...labels, user: r.name || r.email })}
          />

          <SectionTitle
            caption={`Intent is the classifier's reading of the question; "tools" is what the model actually did. ${t.intentMismatches} tracked questions disagree between the two.`}
          >
            What they ask and do
          </SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <BreakdownTable
              title="Question intent (classifier)"
              rows={data.intents.map((r) => ({
                label: intentLabel(r.intent),
                value: r.questions,
                secondary: formatUsd(r.costUsd),
                onClick: () => {
                  setIntent(r.intent === "unclassified" ? "" : r.intent);
                  setPage(0);
                },
              }))}
            />
            <BreakdownTable
              title="What the model did (tools)"
              rows={data.toolIntents.map((r) => ({
                label: intentLabel(r.intent),
                value: r.questions,
                secondary: formatUsd(r.costUsd),
              }))}
            />
            <BreakdownTable
              title="Actions"
              rows={data.actions.map((r) => ({ label: ACTION_LABELS[r.type] ?? r.type, value: r.count }))}
            />
            <BreakdownTable
              title="Clicked link targets"
              rows={data.clickedEntityTypes.map((r) => ({ label: r.entityType, value: r.count }))}
            />
            <BreakdownTable
              title="Tool calls"
              rows={data.tools.map((r) => ({
                label: r.name,
                value: r.calls,
                secondary: `${r.errors} errors · avg ${formatMs(r.avgMs)}`,
              }))}
            />
            <BreakdownTable
              title="Outcomes"
              rows={data.statuses.map((r) => ({ label: r.status, value: r.count }))}
            />
          </Box>

          <SectionTitle caption="Click a row to open its conversation.">Most expensive questions</SectionTitle>
          <SortableTable
            rows={data.topCostTurns}
            columns={costColumns}
            rowKey={(r) => r.turnId}
            defaultSort="costUsd"
            onRowClick={(r) => r.conversationId && setOpenConversation(r.conversationId)}
          />
        </Stack>
      )}

      <Stack spacing={2} sx={{ mt: 3 }}>
        <Stack direction="row" alignItems="flex-end" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <SectionTitle caption="Latest first. Click a row to read the full conversation with per-question cost and tools.">
            Conversations
          </SectionTitle>
          <TextField
            select
            size="small"
            label="Intent"
            value={intent}
            onChange={(e) => {
              setIntent(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All intents</MenuItem>
            {Object.entries(INTENT_LABELS)
              .filter(([key]) => !["unclassified", "unknown"].includes(key))
              .map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
          </TextField>
        </Stack>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Company</TableCell>
                <TableCell align="right">Questions</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Clicks</TableCell>
                <TableCell align="right">Collections</TableCell>
                <TableCell>Top intent</TableCell>
                <TableCell>Last question</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversationsQuery.isFetching && !conversationsQuery.data && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {conversationsQuery.data?.conversations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary">
                      No conversations in this range.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {conversationsQuery.data?.conversations.map((c) => (
                <TableRow
                  key={c.conversationId}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setOpenConversation(c.conversationId)}
                >
                  <TableCell sx={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.deletedAt && (
                      <MuiTooltip title={`Deleted ${formatDateTime(c.deletedAt)}`}>
                        <Chip size="small" label="Deleted" sx={{ mr: 1 }} />
                      </MuiTooltip>
                    )}
                    {c.title}
                  </TableCell>
                  <TableCell>{c.userName}</TableCell>
                  <TableCell>{c.companyName}</TableCell>
                  <TableCell align="right">
                    {c.turns}
                    {c.errors > 0 && (
                      <Typography component="span" variant="caption" color="error" sx={{ ml: 0.5 }}>
                        ({c.errors} err)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatUsd(c.costUsd)}</TableCell>
                  <TableCell align="right">{c.clicks}</TableCell>
                  <TableCell align="right">{c.collectionsSaved}</TableCell>
                  <TableCell>{intentLabel(c.topIntent)}</TableCell>
                  <TableCell>{formatDateTime(c.lastAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={conversationsQuery.data?.total ?? 0}
            page={page}
            onPageChange={(_e, next) => setPage(next)}
            rowsPerPage={CONVERSATIONS_PAGE_SIZE}
            rowsPerPageOptions={[CONVERSATIONS_PAGE_SIZE]}
          />
        </TableContainer>
      </Stack>

      <ConversationDrawer conversationId={openConversation} onClose={() => setOpenConversation(null)} />
    </Container>
  );
}
